import { layoutTokens, typographyTokens, OFF_WHITE, ALERT_RED } from "@/shared/theme";
import { alpha } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  chessArrow,
  type ChessArrow as Arrow,
  type ChessSquareStyles as CustomSquareStyles,
  type ChessSquare as Square,
  useEvaluationGutterWidth,
} from "@/shared/components/chessboard";
import { fetchGameAnalysis, type AnalyzedMoveResult } from "@/shared/api/engine.api";
import { ApiRequestError } from "@/shared/api/client";
import type { BoardEvidenceFocus } from "@/shared/components/chessboard/BoardEvidenceWheel";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import { useBoardPreferences, REVIEW_EVALUATION_DEPTH, BoardMoveControls, useBoardNotice } from "@/features/board";
import { dedupeArrows } from "@/features/board/review/arrow-geometry";
import { BoardDisplay } from "./board/BoardDisplay";
import { GameDetailsEvidenceMatrix } from "./board/GameDetailsEvidenceMatrix";
import { MoveList } from "./board/MoveList";
import type { MovePairRow } from "./board/MoveList";
import { GameNikolaPanel } from "./board/GameNikolaPanel";
import { playChessMoveSound } from "@/shared/utils/chess-sound";
import type * as OpeningBook from "@/features/openings";
import {
  canPlayerMove,
  legalTargetsFrom,
  temporarySquareStyles,
  useTemporaryPlay,
  TemporaryPlayRail,
} from "@/features/board/temporary-play";
import {
  displayedEvaluation,
  exactTemporaryEvaluation,
  hasUsableEvaluation,
  normalizeEvaluation,
} from "@/shared/components/evaluation";
import { useProgressiveEvaluation } from "@/features/board/review/use-progressive-evaluation";
import {
  canExploreReviewDecision,
  isReviewedPlayerTurn,
  matchesReviewDecision,
  shouldRunLiveReviewEvaluation,
  type ReviewDecisionPosition,
} from "./review-decision";
import { GameEvidenceTimeline } from "./board/GameEvidenceTimeline";
import {
  matchingMoveEvidence,
  MoveEvidenceStrip,
  moveEvidenceBoardPresentation,
  moveEvidenceEvaluation,
  PLAYED_MOVE_ARROW_COLOR,
  PLAYED_MOVE_SQUARE_TINT,
  moveEvidencePosition,
  visibleMoveEvidence,
  type MoveEvidenceLayer,
} from "./board/MoveEvidenceStrip";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import type { WinningOutcomeMotif } from "./game-outcome";
import { evaluatedLineStore, type BoardSegment } from "@/features/chat";
import {
  nikolaBoardStepSan,
  resolveNikolaBoardCommand,
  stepNikolaBoardCommand,
  verifiedContinuationCommand,
  type NikolaBoardCommand,
} from "./board/nikola-board-command";
import { GameReviewWorkspace } from "./board/GameReviewTabs";
import { reviewMoveIndex, type GameReviewTab, type ReviewMoveSelection } from "./review-navigation";
import { temporaryMoveEvidence } from "./board/temporary-move-evidence";
import { createVerifiedLine } from "@/features/board/review/verified-line";
import { useProfileStore } from "@/features/profile";
import { GamePlayerRow } from "./board/GamePlayerRow";
import { recordedPlayerClocks } from "./board/player-clock";

/**
 * Standalone game board + move list + action buttons.
 *
 * Driven entirely by the `gameId` prop — no global store dependency —
 * so it can be used both inside ChatPage's sidebar (GameSidePanel, which
 * wraps this and reads gameId from the board store) and as the primary
 * interface on the standalone /games/:id review page (GameReviewPage).
 *
 * Move explanations are compiled locally from verified engine and board facts.
 */

interface GameBoardProps {
  gameId: string;
  exactTimeControl?: string | null;
  navigation: ReactNode;
  activeTab: GameReviewTab;
  onTabChange: (tab: GameReviewTab) => void;
  reviewPanel: ReactNode;
  detailsPanel: ReactNode;
  initialMove?: ReviewMoveSelection | null;
  decisionPosition?: ReviewDecisionPosition | null;
  /** Width of the board in pixels. If omitted, auto-sizes to the wrapper. */
  fixedWidth?: number;
  /** Called when the selected move index changes — lets the parent sync other UI. */
  onMoveChange?: (move: AnalyzedMoveResult | null, index: number, moves: AnalyzedMoveResult[]) => void;
  onRecordedMoveSelect?: (move: AnalyzedMoveResult | null) => void;
  onShowOpponentAnalysisChange?: (showOpponentAnalysis: boolean) => void;
  revealEvidence?: boolean;
  allowTemporaryPlay?: boolean;
  temporaryInteractive?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onAnalysisMovesChange?: (moves: AnalyzedMoveResult[]) => void;
  onAnalysisStateChange?: (state: "loading" | "ready" | "missing" | "error") => void;
  outcomeMotif?: WinningOutcomeMotif | null;
}

const isSquare = (value: string | null | undefined): value is Square =>
  typeof value === "string" && /^[a-h][1-8]$/.test(value);
type PromotionPiece = "q" | "r" | "b" | "n";
interface PendingPromotion {
  from: Square;
  to: Square;
  options: readonly PromotionPiece[];
}
const promotionNames: Record<PromotionPiece, string> = { q: "Queen", r: "Rook", b: "Bishop", n: "Knight" };

const chronologicalMoves = (moves: AnalyzedMoveResult[]) =>
  [...moves].sort((a, b) => {
    if (a.moveNumber !== b.moveNumber) return a.moveNumber - b.moveNumber;
    const aSide = a.fenBefore.split(" ")[1] === "w" ? 0 : 1;
    const bSide = b.fenBefore.split(" ")[1] === "w" ? 0 : 1;
    return aSide - bSide;
  });

export const GameBoard = ({
  gameId,
  exactTimeControl,
  navigation,
  activeTab,
  onTabChange,
  reviewPanel,
  detailsPanel,
  initialMove = null,
  decisionPosition = null,
  fixedWidth,
  onMoveChange,
  onRecordedMoveSelect,
  onShowOpponentAnalysisChange,
  revealEvidence = true,
  allowTemporaryPlay = true,
  temporaryInteractive = true,
  onLoadingChange,
  onAnalysisMovesChange,
  onAnalysisStateChange,
  outcomeMotif = null,
}: GameBoardProps) => {
  const [moves, setMoves] = useState<AnalyzedMoveResult[]>([]);
  const profile = useProfileStore((state) => state.profile);
  const [opponent, setOpponent] = useState<string | null>(null);
  const [opponentRating, setOpponentRating] = useState<number | null>(null);
  const [playerRating, setPlayerRating] = useState<number | null>(null);
  const [timeControl, setTimeControl] = useState<string | null>(null);
  const [analysisDepth, setAnalysisDepth] = useState(0);
  const [orientation, setOrientation] = useState<"white" | "black">("white");
  const [index, setIndex] = useState(-1);
  const [evidenceLayer, setEvidenceLayer] = useState<MoveEvidenceLayer>("played");
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [nikolaBoardCommand, setNikolaBoardCommand] = useState<NikolaBoardCommand | null>(null);

  const [selectedTemporarySquare, setSelectedTemporarySquare] = useState<Square | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<{ fen: string; square: Square | null } | null>(null);
  const [evidenceFocus, setEvidenceFocus] = useState<BoardEvidenceFocus | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<PendingPromotion | null>(null);
  const { settings: boardSettings, change: changeBoardSettings } = useBoardPreferences("review");
  const [settingsOpen, setSettingsOpen] = useState(false);

  const boardWrapperRef = useRef<HTMLDivElement | null>(null);
  const [boardWidth, setBoardWidth] = useState(480);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const gutter = useEvaluationGutterWidth();

  // Auto-size board to wrapper when no fixedWidth is provided.
  useEffect(() => {
    if (fixedWidth) return;
    const el = boardWrapperRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width - gutter;
      const rail = el.closest<HTMLElement>('[data-testid="game-review-board-rail"]');
      const content = rail?.firstElementChild;
      const square = el.querySelector<HTMLElement>("[data-review-square]");
      const chrome =
        content && square ? content.getBoundingClientRect().height - square.getBoundingClientRect().height : 0;
      const availableHeight = rail && !mobile ? rail.clientHeight - chrome : w;
      if (w > 0) setBoardWidth(Math.max(1, Math.floor(Math.min(w, availableHeight))));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    const rail = el.closest<HTMLElement>('[data-testid="game-review-board-rail"]');
    if (rail) {
      observer.observe(rail);
      if (rail.firstElementChild) observer.observe(rail.firstElementChild);
    }
    return () => observer.disconnect();
  }, [fixedWidth, gutter, isLoading, mobile]);

  // Load game analysis on mount / gameId change.
  useEffect(() => {
    let active = true;
    setIsLoading(true);
    onLoadingChange?.(true);
    onAnalysisStateChange?.("loading");
    setLoadError(false);
    setMoves([]);
    onAnalysisMovesChange?.([]);
    setIndex(-1);
    setNikolaBoardCommand(null);
    fetchGameAnalysis(gameId)
      .then((analysis) => {
        if (!active) return;
        // Keep the UI defensive against analyses written by older backend
        // versions, which could return each full move in player/opponent order.
        setMoves(chronologicalMoves(analysis.moves));
        onAnalysisMovesChange?.(chronologicalMoves(analysis.moves));
        setOpponent(analysis.opponent);
        setOpponentRating(analysis.opponentRating);
        setPlayerRating(analysis.playerRating);
        setTimeControl(analysis.timeControl);
        setAnalysisDepth(analysis.analysisDepth);
        setOrientation(analysis.playerColor === "b" ? "black" : "white");
        onAnalysisStateChange?.("ready");
        // A review always starts at the initial position. Selecting a notable
        // move automatically made the board open with an unexplained arrow.
        setIndex(-1);
      })
      .catch((error) => {
        if (!active) return;
        setLoadError(true);
        onAnalysisStateChange?.(error instanceof ApiRequestError && error.status === 404 ? "missing" : "error");
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
          onLoadingChange?.(false);
        }
      });
    return () => {
      active = false;
    };
  }, [gameId, onAnalysisStateChange, onLoadingChange, onAnalysisMovesChange]);

  useEffect(() => {
    if (initialMove == null || moves.length === 0) return;
    const initialIndex = reviewMoveIndex(moves, initialMove);
    if (initialIndex != null) setIndex(initialIndex);
  }, [initialMove, moves]);

  const selectMove = useCallback(
    (nextIndex: number) => {
      const boundedIndex = Math.max(-1, Math.min(moves.length - 1, nextIndex));
      setNikolaBoardCommand(null);
      setSelectedMarker(null);
      onRecordedMoveSelect?.(boundedIndex >= 0 ? moves[boundedIndex] : null);
      setEvidenceLayer("played");
      if (boundedIndex === index) return;
      setIndex(boundedIndex);
      if (boardSettings.moveSounds && boundedIndex >= 0) playChessMoveSound(moves[boundedIndex]?.san);
    },
    [index, moves, onRecordedMoveSelect, boardSettings.moveSounds],
  );

  const handleNikolaBoardCommand = useCallback(
    (segment: BoardSegment) => {
      const line = segment.evalLineId ? evaluatedLineStore.get(segment.evalLineId) : undefined;
      const command = resolveNikolaBoardCommand(segment, gameId, moves, line);
      if (!command) return;
      setNikolaBoardCommand(command);
      if (command.moveIndex == null) return;
      setIndex(command.moveIndex);
      onRecordedMoveSelect?.(moves[command.moveIndex] ?? null);
    },
    [gameId, moves, onRecordedMoveSelect],
  );
  const focusBoard = useCallback(() => {
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const animationsEnabled = useUserSettingsStore.getState().settings.animationsEnabled;
    boardWrapperRef.current?.scrollIntoView({
      behavior: reducedMotion || !animationsEnabled ? "auto" : "smooth",
      block: "start",
    });
  }, []);

  // Derived overlays belong to one exact game, ply, and disclosure mode.
  useEffect(() => {
    setEvidenceLayer("played");
    setSelectedTemporarySquare(null);
    setPendingPromotion(null);
  }, [gameId, index, revealEvidence]);

  // Notify parent of move changes.
  const storedCurrentMove = index >= 0 ? moves[index] : null;
  const [openingBook, setOpeningBook] = useState<typeof OpeningBook | null>(null);
  useEffect(() => {
    let active = true;
    void import("@/features/openings")
      .then((book) => {
        if (active) setOpeningBook(book);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  const currentMove = useMemo(() => {
    if (!storedCurrentMove) return null;
    const motifs = openingBook
      ? openingBook.deriveVerifiedBookMotifs(
          storedCurrentMove.motifs,
          openingBook.lookupBookMove(
            storedCurrentMove.fenBefore,
            `${storedCurrentMove.playedFrom}${storedCurrentMove.playedTo}`,
          ),
        )
      : storedCurrentMove.motifs.filter((motif) => motif !== "book");
    return { ...storedCurrentMove, motifs };
  }, [openingBook, storedCurrentMove]);
  const playVerifiedContinuation = useCallback(() => {
    if (!currentMove) return;
    const command = verifiedContinuationCommand(gameId, index, currentMove);
    if (!command) return;
    setNikolaBoardCommand(command);
    focusBoard();
  }, [currentMove, focusBoard, gameId, index]);
  useEffect(() => {
    if (!nikolaBoardCommand?.autoplay || nikolaBoardCommand.step >= nikolaBoardCommand.stepCount - 1) return;
    const timer = window.setTimeout(() => {
      setNikolaBoardCommand((command) =>
        command === nikolaBoardCommand ? stepNikolaBoardCommand(command, command.step + 1) : command,
      );
    }, 700);
    return () => window.clearTimeout(timer);
  }, [nikolaBoardCommand]);
  const previousNikolaBoardCommand = useRef<NikolaBoardCommand | null>(null);
  useEffect(() => {
    const san = nikolaBoardStepSan(previousNikolaBoardCommand.current, nikolaBoardCommand);
    previousNikolaBoardCommand.current = nikolaBoardCommand;
    if (boardSettings.moveSounds && san) playChessMoveSound(san);
  }, [nikolaBoardCommand, boardSettings.moveSounds]);
  useEffect(() => setEvidenceLayer("played"), [currentMove?.evidence?.identity.ply]);
  useEffect(() => {
    onMoveChange?.(currentMove, index, moves);
  }, [currentMove, index, moves, onMoveChange]);

  useEffect(
    () => onShowOpponentAnalysisChange?.(boardSettings.showOpponentAnalysis),
    [boardSettings.showOpponentAnalysis, onShowOpponentAnalysisChange],
  );

  // index -1 is the initial position; index N is the position after ply N.
  // This makes every Next click advance exactly one visible move.
  const playerColor: "w" | "b" = orientation === "black" ? "b" : "w";
  const alternateEvidenceLayer = revealEvidence && currentMove?.evidence != null && evidenceLayer === "better";
  const reviewedFen =
    nikolaBoardCommand?.fen ??
    decisionPosition?.fen ??
    (currentMove ? currentMove.fenAfter : (moves[0]?.fenBefore ?? "start"));
  const currentMoveMatchesDecision = matchesReviewDecision(decisionPosition, currentMove);
  const temporaryAllowed =
    allowTemporaryPlay &&
    isReviewedPlayerTurn(reviewedFen, playerColor) &&
    (decisionPosition == null || canExploreReviewDecision(decisionPosition, playerColor));
  const displayedVerifiedLine = useMemo(() => {
    if (nikolaBoardCommand)
      return createVerifiedLine(
        reviewedFen,
        nikolaBoardCommand.verifiedContinuation?.moves.slice(nikolaBoardCommand.step) ?? [],
        nikolaBoardCommand.verifiedContinuation?.depth ?? null,
      );
    if (!storedCurrentMove?.bestMoveSan || reviewedFen !== storedCurrentMove.fenBefore) return null;
    return createVerifiedLine(
      reviewedFen,
      [storedCurrentMove.bestMoveSan, ...(storedCurrentMove.bestContinuation ?? [])],
      analysisDepth,
    );
  }, [
    analysisDepth,
    nikolaBoardCommand,
    reviewedFen,
    storedCurrentMove?.bestContinuation,
    storedCurrentMove?.bestMoveSan,
    storedCurrentMove?.fenBefore,
  ]);
  const temporaryOrigin = useMemo(
    () =>
      temporaryAllowed &&
      timeControl === "rapid" &&
      reviewedFen !== "start" &&
      playerRating != null &&
      opponentRating != null
        ? {
            id: `${gameId}:${index}:${decisionPosition?.factId ?? "review"}:${nikolaBoardCommand?.key ?? "recorded"}:${nikolaBoardCommand?.step ?? 0}`,
            fen: reviewedFen,
            format: "rapid" as const,
            playerColor,
            playerRating,
            opponentRating,
            verifiedLine: displayedVerifiedLine,
            recordedSan: currentMoveMatchesDecision ? (currentMove?.san ?? null) : null,
            verifiedAlternativeSan: currentMoveMatchesDecision ? (currentMove?.bestMoveSan ?? null) : null,
          }
        : null,
    [
      displayedVerifiedLine,
      currentMove?.bestMoveSan,
      currentMove?.san,
      currentMoveMatchesDecision,
      decisionPosition?.factId,
      gameId,
      index,
      nikolaBoardCommand?.key,
      nikolaBoardCommand?.step,
      opponentRating,
      playerColor,
      playerRating,
      reviewedFen,
      temporaryAllowed,
      timeControl,
    ],
  );
  const temporaryPlay = useTemporaryPlay(
    temporaryOrigin,
    REVIEW_EVALUATION_DEPTH[boardSettings.evaluationPreset],
    boardSettings.moveSounds,
  );
  const displayFen = temporaryPlay && temporaryPlay.state.moves.length > 0 ? temporaryPlay.state.fen : reviewedFen;
  const temporaryActive = temporaryPlay != null && temporaryPlay.state.moves.length > 0;
  const { notify, notice } = useBoardNotice(`${gameId}:${temporaryOrigin?.id}:${displayFen}`);
  useEffect(() => {
    setSelectedTemporarySquare(null);
    setPendingPromotion(null);
  }, [temporaryOrigin?.id, temporaryPlay?.state]);
  const canPlayTemporary =
    !alternateEvidenceLayer &&
    (temporaryInteractive || nikolaBoardCommand != null) &&
    temporaryPlay != null &&
    pendingPromotion == null &&
    canPlayerMove(temporaryPlay.state);
  const selectedTargets =
    canPlayTemporary && selectedTemporarySquare ? legalTargetsFrom(displayFen, selectedTemporarySquare) : [];
  const lastReply =
    temporaryPlay && temporaryPlay.state.moves.length >= 2 && temporaryPlay.state.moves.length % 2 === 0
      ? temporaryPlay.state.moves.at(-1)?.uci
      : undefined;
  const temporaryStyles = temporarySquareStyles(
    boardSettings.showLegalMoves ? selectedTargets : [],
    lastReply,
    selectedTemporarySquare,
  );
  const handlePieceDrop = canPlayTemporary
    ? (source: string, target: string): boolean => {
        setSelectedTemporarySquare(null);
        const destination = legalTargetsFrom(displayFen, source as Square).find(({ square }) => square === target);
        if (!destination) {
          if (source !== target) notify();
          return false;
        }
        if (destination.promotions.length > 1) {
          setPendingPromotion({ from: source as Square, to: target as Square, options: destination.promotions });
          return false;
        }
        void temporaryPlay.move(source, target, destination.promotions[0]);
        return true;
      }
    : undefined;
  const handleSquareClick = (square: Square): void => {
    setSelectedMarker((current) =>
      current?.fen === evidenceDisplayFen && current.square === square ? null : { fen: evidenceDisplayFen, square },
    );
    if (!temporaryPlay) return;
    if (!canPlayTemporary) {
      setSelectedTemporarySquare(null);
      return;
    }
    if (!selectedTemporarySquare) {
      setSelectedTemporarySquare(legalTargetsFrom(displayFen, square).length > 0 ? square : null);
      return;
    }
    const target = selectedTargets.find((candidate) => candidate.square === square);
    setSelectedTemporarySquare(null);
    if (!target) {
      if (legalTargetsFrom(displayFen, square).length > 0) setSelectedTemporarySquare(square);
      else if (square !== selectedTemporarySquare) notify();
      return;
    }
    if (target.promotions.length > 1) {
      setPendingPromotion({ from: selectedTemporarySquare, to: square, options: target.promotions });
      return;
    }
    void temporaryPlay.move(selectedTemporarySquare, square, target.promotions[0]);
  };

  // Match stored evaluation to the exact position shown.
  const storedEvalCp = (() => {
    if (nikolaBoardCommand) {
      const move = nikolaBoardCommand.moveIndex == null ? null : moves[nikolaBoardCommand.moveIndex];
      if (move?.fenBefore === reviewedFen && move.evalBeforeCp != null) return move.evalBeforeCp;
      if (move?.fenAfter === reviewedFen && move.evalAfterCp != null) return move.evalAfterCp;
      return null;
    }
    if (currentMoveMatchesDecision && currentMove?.evalBeforeCp != null) return currentMove.evalBeforeCp;
    if (decisionPosition) return null;
    if (currentMove?.evalAfterCp != null) return currentMove.evalAfterCp;
    if (index === -1 && moves[0]?.evalBeforeCp != null) return moves[0].evalBeforeCp;
    return null;
  })();
  const storedMateIn = (() => {
    if (nikolaBoardCommand) {
      const move = nikolaBoardCommand.moveIndex == null ? null : moves[nikolaBoardCommand.moveIndex];
      if (move?.fenBefore === reviewedFen && move.evalBeforeMate != null) return move.evalBeforeMate;
      if (move?.fenAfter === reviewedFen && move.evalAfterMate != null) return move.evalAfterMate;
      return null;
    }
    if (currentMoveMatchesDecision && currentMove?.evalBeforeMate != null) return currentMove.evalBeforeMate;
    if (decisionPosition) return null;
    if (currentMove?.evalAfterMate != null) return currentMove.evalAfterMate;
    if (index === -1 && moves[0]?.evalBeforeMate != null) return moves[0].evalBeforeMate;
    return null;
  })();

  const progressiveEvaluation = useProgressiveEvaluation({
    fen: displayFen,
    maxDepth: REVIEW_EVALUATION_DEPTH[boardSettings.evaluationPreset],
    enabled: shouldRunLiveReviewEvaluation({
      revealEvidence,
      fen: displayFen,
      storedCp: temporaryActive ? null : storedEvalCp,
      storedMate: temporaryActive ? null : storedMateIn,
    }),
  });
  const liveEvaluation = progressiveEvaluation.evaluation
    ? { fen: displayFen, evaluation: progressiveEvaluation.evaluation }
    : null;

  const currentEvidence = visibleMoveEvidence(
    matchingMoveEvidence(currentMove, currentMove?.evidence),
    revealEvidence,
    temporaryActive,
    nikolaBoardCommand != null,
  );
  const evidencePresentation = useMemo(
    () =>
      currentEvidence
        ? moveEvidenceBoardPresentation(currentEvidence, evidenceLayer)
        : { arrows: [], squareStyles: {}, description: "" },
    [currentEvidence, evidenceLayer],
  );
  const evidenceDisplayFen = currentEvidence ? moveEvidencePosition(currentEvidence, evidenceLayer) : displayFen;
  useEffect(() => setSelectedMarker(null), [evidenceDisplayFen, gameId]);
  const latestTemporaryPlayerMoveIndex = temporaryPlay?.state.moves.length
    ? Math.floor((temporaryPlay.state.moves.length - 1) / 2) * 2
    : -1;
  const latestTemporaryPlayerMove =
    latestTemporaryPlayerMoveIndex >= 0 ? (temporaryPlay?.state.moves[latestTemporaryPlayerMoveIndex] ?? null) : null;
  const latestTemporaryBeforeFen =
    latestTemporaryPlayerMoveIndex > 0
      ? (temporaryPlay?.state.moves[latestTemporaryPlayerMoveIndex - 1]?.fen ?? null)
      : (temporaryPlay?.state.origin.fen ?? null);
  const bookEvidence = temporaryActive
    ? latestTemporaryBeforeFen && latestTemporaryPlayerMove
      ? openingBook?.lookupBookMove(latestTemporaryBeforeFen, latestTemporaryPlayerMove.uci)
      : null
    : storedCurrentMove
      ? openingBook?.lookupBookMove(
          storedCurrentMove.fenBefore,
          `${storedCurrentMove.playedFrom}${storedCurrentMove.playedTo}`,
        )
      : null;
  const temporaryEvidence = useMemo(
    () =>
      latestTemporaryBeforeFen
        ? temporaryMoveEvidence(
            latestTemporaryBeforeFen,
            latestTemporaryPlayerMove,
            temporaryPlay?.state.grade ?? null,
            Boolean(
              latestTemporaryPlayerMove &&
              openingBook?.lookupBookMove(latestTemporaryBeforeFen, latestTemporaryPlayerMove.uci),
            ),
          )
        : null,
    [latestTemporaryBeforeFen, latestTemporaryPlayerMove, temporaryPlay?.state.grade, openingBook],
  );
  const temporaryGradeEvaluation =
    temporaryActive &&
    temporaryPlay.state.grade?.status === "ready" &&
    temporaryPlay.state.grade.evaluation &&
    latestTemporaryPlayerMove
      ? {
          fen: latestTemporaryPlayerMove.fen,
          evaluation: normalizeEvaluation(temporaryPlay.state.grade.evaluation, playerColor)!,
        }
      : null;
  const storedEvaluationCandidate =
    storedEvalCp == null && storedMateIn == null
      ? null
      : {
          cp: storedMateIn == null ? storedEvalCp : null,
          mate: storedMateIn,
        };
  const storedEvaluation = hasUsableEvaluation(storedEvaluationCandidate) ? storedEvaluationCandidate : null;
  const exactLiveEvaluation = liveEvaluation?.fen === displayFen ? liveEvaluation.evaluation : null;
  const historicalEvaluation =
    normalizeEvaluation(storedEvaluation, reviewedFen.split(" ")[1] === "b" ? "b" : "w") ?? exactLiveEvaluation;
  const temporaryEvaluation = exactTemporaryEvaluation(displayFen, liveEvaluation, temporaryGradeEvaluation);
  const recordedEvaluation = displayedEvaluation(historicalEvaluation, temporaryEvaluation, temporaryActive);
  const evidenceEvaluation = currentEvidence
    ? normalizeEvaluation(moveEvidenceEvaluation(currentEvidence, evidenceLayer), playerColor)
    : null;
  const evaluation = currentEvidence ? evidenceEvaluation : recordedEvaluation;
  const showDecisionEvidence = decisionPosition != null && !temporaryActive;
  const decisionArrows: Arrow[] =
    showDecisionEvidence && decisionPosition.playedFrom && decisionPosition.playedTo
      ? [
          chessArrow(decisionPosition.playedFrom, decisionPosition.playedTo, PLAYED_MOVE_ARROW_COLOR, {
            label: "Played move",
            description: `${decisionPosition.playedSan}: ${decisionPosition.playedFrom} → ${decisionPosition.playedTo}.`,
          }),
        ]
      : [];
  const decisionHighlights: CustomSquareStyles = showDecisionEvidence
    ? Object.fromEntries(
        [decisionPosition.playedFrom, decisionPosition.playedTo]
          .filter(isSquare)
          .map((square) => [square, { backgroundColor: PLAYED_MOVE_SQUARE_TINT }]),
      )
    : {};

  // Move list grouping.
  const movePairs: MovePairRow[] = [];
  moves.forEach((m, i) => {
    let row = movePairs.find((r) => r.moveNumber === m.moveNumber);
    if (!row) {
      row = { moveNumber: m.moveNumber, white: null, black: null, whiteIdx: -1, blackIdx: -1 };
      movePairs.push(row);
    }
    const side = m.fenBefore.split(" ")[1];
    if (side === "w") {
      row.white = m;
      row.whiteIdx = i;
    } else {
      row.black = m;
      row.blackIdx = i;
    }
  });
  const markerFocus = evidenceFocus?.fen === evidenceDisplayFen ? evidenceFocus : selectedMarker;
  const nikolaArrows = (nikolaBoardCommand?.arrows ?? []).flatMap(({ from, to, color }) =>
    isSquare(from) && isSquare(to)
      ? [
          chessArrow(from, to, color ?? OFF_WHITE.main, {
            label: "Nikola annotation",
            description: `Nikola marked ${from} → ${to} in this position.`,
          }),
        ]
      : [],
  );
  const nikolaHighlights = Object.fromEntries(
    (nikolaBoardCommand?.highlights ?? [])
      .filter(({ square }) => isSquare(square))
      .map(({ square, color }) => [square, { backgroundColor: color ?? alpha(ALERT_RED.main, 0.35) }]),
  ) as CustomSquareStyles;
  const configuredReviewArrows =
    nikolaBoardCommand && !temporaryActive ? dedupeArrows(nikolaArrows) : dedupeArrows(decisionArrows);
  const configuredReviewHighlights = nikolaBoardCommand && !temporaryActive ? nikolaHighlights : decisionHighlights;

  if (isLoading) {
    return onLoadingChange ? null : <ChesslaLoadingMark page="Review" label="Preparing your game review" />;
  }

  if (loadError) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 3 }}>
        <Typography variant="body2" sx={{ color: "secondary.main" }}>
          Could not load this game. It may not have been analyzed yet.
        </Typography>
      </Box>
    );
  }

  const clocks = recordedPlayerClocks(
    moves,
    temporaryActive || nikolaBoardCommand ? -1 : evidenceDisplayFen === currentMove?.fenBefore ? index - 1 : index,
    temporaryActive || nikolaBoardCommand ? null : exactTimeControl,
  );
  const decisionLabel =
    !nikolaBoardCommand && decisionPosition && currentMoveMatchesDecision
      ? revealEvidence
        ? `BEFORE ${decisionPosition.playedSan} · TEST YOUR IDEA`
        : "TEST YOUR IDEA FROM THIS POSITION"
      : null;
  return (
    <GameReviewWorkspace
      navigation={navigation}
      activeTab={activeTab}
      onChange={onTabChange}
      unavailable={
        !revealEvidence
          ? {
              nikola: "Finish testing your idea, then continue to Verify.",
              moves: "The game continuation opens at the Verify step.",
            }
          : undefined
      }
      feedback={temporaryActive ? <TemporaryPlayRail controller={temporaryPlay} revealGrade={revealEvidence} /> : null}
      board={
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Board */}
          <BoardDisplay
            relationshipFocus={markerFocus?.square ? { fen: markerFocus.fen, square: markerFocus.square } : null}
            relationshipArrows={!nikolaBoardCommand && evidenceDisplayFen !== "start"}
            playerRows={{
              top: (
                <GamePlayerRow
                  side="opponent"
                  color={playerColor === "w" ? "b" : "w"}
                  fen={evidenceDisplayFen}
                  username={opponent ?? ""}
                  rating={opponentRating}
                  clock={clocks.opponent}
                />
              ),
              bottom: (
                <GamePlayerRow
                  side="player"
                  color={playerColor}
                  fen={evidenceDisplayFen}
                  username={profile?.username ?? ""}
                  avatar={profile?.chessCom.avatar}
                  rating={playerRating}
                  clock={clocks.player}
                />
              ),
            }}
            bookReference={openingBook?.bookMoveReference(bookEvidence ?? null)}
            fen={evidenceDisplayFen}
            orientation={orientation}
            boardWidth={fixedWidth ?? boardWidth}
            arrows={configuredReviewArrows}
            squareStyles={configuredReviewHighlights}
            evidenceArrows={evidencePresentation.arrows}
            evidenceSquareStyles={evidencePresentation.squareStyles}
            evidenceDescription={evidencePresentation.description}
            boardId={`game-board-${gameId}`}
            boardWrapperRef={boardWrapperRef}
            evaluation={evaluation}
            evaluationPending={progressiveEvaluation.pending && evaluation == null}
            currentMove={nikolaBoardCommand || temporaryActive ? null : currentMove}
            outcomeMotif={index === moves.length - 1 ? outcomeMotif : null}
            includeOpponentEvidence={boardSettings.showOpponentAnalysis}
            decisionLabel={decisionLabel}
            notice={notice}
            temporaryPlay={temporaryPlay}
            promotionDialog={
              pendingPromotion &&
              temporaryPlay && (
                <Box
                  role="dialog"
                  aria-modal="true"
                  aria-label="Choose promotion piece"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                    rowGap: `${layoutTokens.dividerBorder}px`,
                    border: layoutTokens.dividerBorder,
                    borderColor: "divider",
                  }}
                >
                  {pendingPromotion.options.map((promotion) => (
                    <Button
                      key={promotion}
                      aria-label={`Promote to ${promotionNames[promotion]}`}
                      onClick={() => {
                        const { from, to } = pendingPromotion;
                        setPendingPromotion(null);
                        void temporaryPlay.move(from, to, promotion);
                      }}
                      sx={{ minWidth: 0, minHeight: layoutTokens.controlHeight, borderRadius: 0 }}
                    >
                      {promotionNames[promotion]}
                    </Button>
                  ))}
                  <Button
                    aria-label="Cancel promotion"
                    onClick={() => setPendingPromotion(null)}
                    sx={{ gridColumn: "1 / -1", minHeight: layoutTokens.controlHeight, borderRadius: 0 }}
                  >
                    Cancel
                  </Button>
                </Box>
              )
            }
            temporarySquareStyles={temporaryStyles}
            temporaryEvidence={temporaryEvidence}
            onPlayVerifiedLine={playVerifiedContinuation}
            onActiveChange={setEvidenceFocus}
            onPieceDrop={handlePieceDrop}
            onSquareClick={handleSquareClick}
            revealEvidence={revealEvidence}
            showMotifs={boardSettings.showMotifs}
            settings={boardSettings}
            settingsOpen={settingsOpen}
            onSettingsClose={() => setSettingsOpen(false)}
            onSettingsChange={(settings) => {
              if (settings.showAllPositionMarkers === true)
                setSelectedMarker({ fen: evidenceDisplayFen, square: null });
              changeBoardSettings(settings);
            }}
          />
          <BoardMoveControls
            label={
              temporaryActive
                ? `IMAGINARY · ${temporaryPlay.state.moves.length} plies`
                : nikolaBoardCommand
                  ? `VERIFIED · ${nikolaBoardCommand.title}`
                  : (decisionLabel ??
                    (currentMove ? `MOVE ${currentMove.moveNumber} · ${currentMove.san}` : "START POSITION"))
            }
            position={temporaryActive ? 0 : nikolaBoardCommand ? nikolaBoardCommand.step : index + 1}
            count={temporaryActive ? 0 : nikolaBoardCommand ? nikolaBoardCommand.stepCount - 1 : moves.length}
            onNavigate={(position) => {
              if (nikolaBoardCommand)
                setNikolaBoardCommand({ ...stepNikolaBoardCommand(nikolaBoardCommand, position), autoplay: false });
              else selectMove(position - 1);
            }}
            onRestore={
              temporaryActive || nikolaBoardCommand
                ? () => {
                    temporaryPlay?.reset();
                    setNikolaBoardCommand(null);
                  }
                : undefined
            }
            onTogglePlay={
              nikolaBoardCommand && !temporaryActive && nikolaBoardCommand.stepCount > 1
                ? () =>
                    setNikolaBoardCommand((command) =>
                      command
                        ? {
                            ...stepNikolaBoardCommand(
                              command,
                              command.step === command.stepCount - 1 ? 0 : command.step,
                            ),
                            autoplay: command.step === command.stepCount - 1 || !command.autoplay,
                          }
                        : command,
                    )
                : undefined
            }
            playing={Boolean(
              nikolaBoardCommand?.autoplay && nikolaBoardCommand.step < nikolaBoardCommand.stepCount - 1,
            )}
            onSettings={() => setSettingsOpen(true)}
            disabled={pendingPromotion != null}
          />
        </Box>
      }
      review={reviewPanel}
      nikola={
        revealEvidence ? (
          <GameNikolaPanel gameId={gameId} currentMove={currentMove} onBoardCommand={handleNikolaBoardCommand} />
        ) : (
          <Box sx={{ borderBlock: layoutTokens.dividerBorder, borderColor: "divider", py: 3 }}>
            <Typography
              sx={{
                color: "secondary.main",
                fontSize: "caption.fontSize",
                fontWeight: "fontWeightBold",
                letterSpacing: typographyTokens.tracking.label,
              }}
            >
              NIKOLA / VERIFY FIRST
            </Typography>
            <Typography
              sx={{
                mt: 1,
                maxWidth: layoutTokens.detailMaxWidth,
                color: "text.secondary",
                lineHeight: typographyTokens.lineHeight.body,
              }}
            >
              Test your idea before Nikola opens the verified evidence for this moment.
            </Typography>
          </Box>
        )
      }
      moves={
        <Box sx={{ minWidth: 0 }}>
          {!revealEvidence && decisionPosition && (
            <Box sx={{ borderBlock: layoutTokens.dividerBorder, borderColor: "divider", py: 3 }}>
              <Typography
                sx={{
                  color: "secondary.main",
                  fontSize: "caption.fontSize",
                  fontWeight: "fontWeightBold",
                  letterSpacing: typographyTokens.tracking.label,
                }}
              >
                MOVES / VERIFY FIRST
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  maxWidth: layoutTokens.detailMaxWidth,
                  color: "text.secondary",
                  lineHeight: typographyTokens.lineHeight.body,
                }}
              >
                The continuation and move evidence stay hidden while you test your own idea.
              </Typography>
            </Box>
          )}
          {currentEvidence && (
            <MoveEvidenceStrip packet={currentEvidence} layer={evidenceLayer} onLayerChange={setEvidenceLayer} />
          )}
          {revealEvidence && (
            <GameEvidenceTimeline
              moves={moves}
              playerColor={playerColor}
              currentIndex={index}
              showMotifs={boardSettings.showMotifs}
              includeOpponentEvidence={boardSettings.showOpponentAnalysis}
              onSelect={selectMove}
            />
          )}
          {(!decisionPosition || revealEvidence) && (
            <MoveList
              movePairs={movePairs}
              currentIndex={index}
              onSelectIndex={selectMove}
              includeOpponentEvidence={boardSettings.showOpponentAnalysis}
            />
          )}
        </Box>
      }
      details={
        <GameDetailsEvidenceMatrix moves={moves} playerColor={playerColor} opponent={opponent}>
          {detailsPanel}
        </GameDetailsEvidenceMatrix>
      }
    />
  );
};
